import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { value: 'admin',  label: 'Admin'  },
  { value: 'editor', label: 'Editor' },
  { value: 'viewer', label: 'Viewer' },
];

const DEFAULT_PERMS_BY_ROLE = {
  viewer: { can_export: false, can_see_pii: false, can_publish: false },
  editor: { can_export: true,  can_see_pii: true,  can_publish: true  },
  admin:  { can_export: true,  can_see_pii: true,  can_publish: true  },
  super_admin: { can_export: true, can_see_pii: true, can_publish: true },
};

export default function UserEditorModal({ open, mode, user, onClose, onSaved }) {
  // mode: 'create' | 'edit'
  const isCreate = mode === 'create';
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [scopeType, setScopeType] = useState('village');
  const [selectedVillages, setSelectedVillages] = useState([]);
  const [permissions, setPermissions] = useState(DEFAULT_PERMS_BY_ROLE.viewer);
  const [villageOptions, setVillageOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Load clients (villages) once when the modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get('/clients');
        if (!cancelled) {
          const list = Array.isArray(resp.data) ? resp.data : (resp.data?.clients || []);
          setVillageOptions(list.map((c) => ({ id: c.id, name: c.name })));
        }
      } catch (e) {
        if (!cancelled) setVillageOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Reset form when opening
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (isCreate) {
      setEmail('');
      setRole('viewer');
      setScopeType('village');
      setSelectedVillages([]);
      setPermissions(DEFAULT_PERMS_BY_ROLE.viewer);
    } else if (user) {
      setEmail(user.email || '');
      setRole(user.role || 'viewer');
      setScopeType(user.scope?.type || 'village');
      setSelectedVillages(user.scope?.village_ids || []);
      setPermissions({
        can_export:  !!user.scope?.permissions?.can_export,
        can_see_pii: !!user.scope?.permissions?.can_see_pii,
        can_publish: !!user.scope?.permissions?.can_publish,
      });
    }
  }, [open, isCreate, user]);

  // When role changes (create mode only), apply role-specific permission defaults
  useEffect(() => {
    if (!isCreate) return;
    if (role && DEFAULT_PERMS_BY_ROLE[role]) {
      setPermissions({ ...DEFAULT_PERMS_BY_ROLE[role] });
    }
  }, [role, isCreate]);

  const toggleVillage = (id) => {
    setSelectedVillages((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (isCreate && (!email || !email.includes('@'))) {
      setError('Voer een geldig e-mailadres in.');
      return;
    }
    if (scopeType === 'village' && selectedVillages.length === 0) {
      setError('Kies minimaal één village voor village-scope.');
      return;
    }

    const scope = {
      type: scopeType,
      village_ids: scopeType === 'village' ? selectedVillages : [],
    };

    setSubmitting(true);
    try {
      if (isCreate) {
        const body = {
          email: email.trim().toLowerCase(),
          role,
          scope,
          permissions,
        };
        const resp = await api.post('/admin/users', body);
        toast.success(`Uitnodiging verstuurd naar ${resp.data.email}`);
        onSaved?.(resp.data);
        onClose?.();
      } else {
        const body = {
          role,
          scope,
          permissions,
        };
        const resp = await api.patch(`/admin/users/${user.id}`, body);
        toast.success('Gebruiker bijgewerkt');
        onSaved?.(resp.data);
        onClose?.();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Er ging iets mis.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="user-editor-modal">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'Gebruiker uitnodigen' : 'Gebruiker bewerken'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="user-email">E-mail</Label>
            <Input
              id="user-email"
              data-testid="user-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!isCreate || submitting}
              required
            />
          </div>
          <div>
            <Label>Rol</Label>
            <Select value={role} onValueChange={setRole} disabled={submitting}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`role-option-${r.value}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">Super_admin kan niet via deze UI worden toegekend.</p>
          </div>
          <div>
            <Label>Scope</Label>
            <div className="flex gap-3 mt-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="scope-type"
                  value="global"
                  checked={scopeType === 'global'}
                  onChange={() => setScopeType('global')}
                  data-testid="scope-type-global"
                />
                Global
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="scope-type"
                  value="village"
                  checked={scopeType === 'village'}
                  onChange={() => setScopeType('village')}
                  data-testid="scope-type-village"
                />
                Village
              </label>
            </div>
          </div>
          {scopeType === 'village' && (
            <div className="border rounded p-3 max-h-44 overflow-auto" data-testid="village-list">
              {villageOptions.length === 0 ? (
                <p className="text-xs text-gray-500">Geen villages beschikbaar.</p>
              ) : (
                villageOptions.map((v) => (
                  <label key={v.id} className="flex items-center gap-2 text-sm py-1">
                    <Checkbox
                      checked={selectedVillages.includes(v.id)}
                      onCheckedChange={() => toggleVillage(v.id)}
                      data-testid={`village-checkbox-${v.id}`}
                    />
                    <span>{v.name}</span>
                  </label>
                ))
              )}
            </div>
          )}
          <div>
            <Label>Permissies</Label>
            <div className="grid grid-cols-1 gap-2 mt-1">
              {[
                { key: 'can_export',  label: 'Mag exports downloaden' },
                { key: 'can_see_pii', label: 'Mag PII zien (e-mail, namen)' },
                { key: 'can_publish', label: 'Mag cursussen publiceren' },
              ].map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!!permissions[p.key]}
                    onCheckedChange={(v) => setPermissions((prev) => ({ ...prev, [p.key]: !!v }))}
                    data-testid={`perm-${p.key}`}
                  />
                  <span>{p.label}</span>
                </label>
              ))}
            </div>
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="user-editor-error">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Annuleren</Button>
            <Button type="submit" disabled={submitting} data-testid="user-editor-submit">
              {submitting ? 'Bezig…' : (isCreate ? 'Uitnodiging versturen' : 'Opslaan')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
